// Admin-only architecture + data-pipeline diagrams. Data-driven SVG (nodes + edges), theme-aware via
// the ATLAS CSS variables so they follow day/night. Anchors are computed from box geometry so edges
// meet sides cleanly. These mirror the deployed system — keep them in sync when the stacks change.

type Node = { id: string; x: number; y: number; w: number; h: number; title: string; sub: string[]; accent?: boolean };
type Edge = { from: string; to: string; dash?: boolean; label?: string; dir?: "h" | "v" };
type Band = { label: string; x: number; y: number };

/* ── System architecture (top-down bands) ───────────────────────────────── */
const ARCH_NODES: Node[] = [
  { id: "entra", x: 858, y: 40, w: 280, h: 54, title: "Azure EntraID", sub: ["App Roles · JWKS"], accent: true },

  { id: "spa", x: 30, y: 132, w: 300, h: 76, title: "React SPA", sub: ["Vite · Router 7 · ATLAS CSS"], accent: true },
  { id: "dexie", x: 352, y: 132, w: 244, h: 76, title: "Dexie / IndexedDB", sub: ["per-visitor journey", "device-local only"] },
  { id: "msal", x: 618, y: 132, w: 252, h: 76, title: "MSAL.js", sub: ["auth-code + PKCE"] },

  { id: "r53", x: 30, y: 256, w: 168, h: 64, title: "Route 53", sub: ["DNS / alias"] },
  { id: "cf", x: 220, y: 256, w: 330, h: 64, title: "CloudFront + WAF", sub: ["managed rules · rate 2k/5m", "CAPTCHA → /api/quota/email"] },
  { id: "s3", x: 572, y: 256, w: 200, h: 64, title: "S3", sub: ["static site (dist)"] },
  { id: "cost", x: 928, y: 256, w: 210, h: 64, title: "Cost controls", sub: ["$50 budget · ChatFn alarms", "→ breaker pauses /chat"] },

  { id: "api", x: 220, y: 372, w: 552, h: 62, title: "API Gateway — HTTP API", sub: ["throttled · EntraID JWT authorizer"], accent: true },

  { id: "lpub", x: 96, y: 484, w: 408, h: 82, title: "Public Lambdas", sub: ["/chat · /quota/* · /ask", "/feedback · breaker"] },
  { id: "ladm", x: 528, y: 484, w: 320, h: 82, title: "Admin Lambdas — JWT", sub: ["/content read · admin · write"] },

  { id: "ddb", x: 30, y: 624, w: 312, h: 88, title: "DynamoDB", sub: ["Questions · ContentEdits", "Feedback · Quota (salted)"] },
  { id: "bedrock", x: 364, y: 624, w: 236, h: 88, title: "Amazon Bedrock", sub: ["Claude Haiku", "Converse · prompt cache"] },
  { id: "ses", x: 622, y: 624, w: 170, h: 88, title: "Amazon SES", sub: ["notify ·", "quota magic-link"] },
  { id: "connect", x: 814, y: 624, w: 252, h: 88, title: "Amazon Connect", sub: ["Talk to me", "browser → live chat"] },

  { id: "yaml", x: 30, y: 764, w: 250, h: 62, title: "resume.yaml", sub: ["source of truth"] },
  { id: "build", x: 300, y: 764, w: 330, h: 62, title: "build scripts", sub: ["→ corpus/*.md · llms.txt"] },
  { id: "reconcile", x: 650, y: 764, w: 330, h: 62, title: "reconcile-overlay", sub: ["overlay → YAML"] },
];

const ARCH_EDGES: Edge[] = [
  { from: "spa", to: "cf" },
  { from: "cf", to: "s3" },
  { from: "cf", to: "api", dash: true, label: "/api/* (CAPTCHA)" },
  { from: "spa", to: "api", label: "fetch" },
  { from: "api", to: "lpub" },
  { from: "api", to: "ladm" },
  { from: "lpub", to: "ddb" },
  { from: "lpub", to: "bedrock", label: "/chat /ask" },
  { from: "lpub", to: "ses" },
  { from: "ladm", to: "ddb" },
  { from: "entra", to: "msal", dash: true, label: "PKCE" },
  { from: "entra", to: "api", dash: true, label: "JWT" },
  { from: "yaml", to: "build", dash: true, label: "source" },
  { from: "build", to: "s3", dash: true, label: "deploy" },
  { from: "ddb", to: "reconcile", dash: true },
  { from: "reconcile", to: "yaml", dash: true, label: "writes" },
];

const ARCH_BANDS: Band[] = [
  { label: "Identity", x: 858, y: 30 },
  { label: "Visitor's browser", x: 30, y: 122 },
  { label: "AWS edge · hosting", x: 30, y: 246 },
  { label: "API", x: 30, y: 362 },
  { label: "Compute · Lambda", x: 30, y: 474 },
  { label: "Data · AI · comms", x: 30, y: 614 },
  { label: "Content pipeline · build-time", x: 30, y: 754 },
];

/* ── Content data pipeline (left → right: sources → curation gate → canonical → build/AI) ── */
const PIPE_NODES: Node[] = [
  { id: "p_resume", x: 30, y: 64, w: 250, h: 58, title: "Comprehensive résumé", sub: ["full master record"] },
  { id: "p_links", x: 30, y: 140, w: 250, h: 58, title: "Accelerator / blog links", sub: ["external references"] },
  { id: "p_folders", x: 30, y: 216, w: 250, h: 72, title: "Local folders", sub: ["detailed engagement", "content · work samples"] },

  { id: "p_review", x: 332, y: 96, w: 262, h: 88, title: "REVIEW workbook (XLSX)", sub: ["two-gate HITL approval", "source ✓ then snippet ✓"], accent: true },
  { id: "p_corpus", x: 332, y: 208, w: 262, h: 64, title: "Curated corpus", sub: ["approved · immutable seed"] },

  { id: "p_yaml", x: 646, y: 96, w: 268, h: 64, title: "resume.yaml", sub: ["canonical source of truth"], accent: true },
  { id: "p_overlay", x: 646, y: 188, w: 268, h: 88, title: "Admin Edit → overlay", sub: ["DynamoDB ContentEdits", "maintained in-app now"], accent: true },

  { id: "p_build", x: 950, y: 64, w: 230, h: 58, title: "build scripts", sub: ["prebuild · privacy gate"] },
  { id: "p_pub", x: 950, y: 140, w: 230, h: 84, title: "Public corpus", sub: ["resume.md · -compact", "engagements · llms.txt"] },
  { id: "p_ai", x: 950, y: 246, w: 230, h: 64, title: "Ask AI · BYO-AI", sub: ["Bedrock grounding"] },
];

const PIPE_EDGES: Edge[] = [
  { from: "p_resume", to: "p_review", dir: "h" },
  { from: "p_links", to: "p_review", dir: "h" },
  { from: "p_folders", to: "p_review", dir: "h" },
  { from: "p_review", to: "p_corpus", dir: "v", label: "approve" },
  { from: "p_corpus", to: "p_yaml", dir: "h", dash: true, label: "seed (originally)" },
  { from: "p_overlay", to: "p_yaml", dir: "v", dash: true, label: "reconcile" },
  { from: "p_yaml", to: "p_build", dir: "h", label: "source" },
  { from: "p_build", to: "p_pub", dir: "v" },
  { from: "p_pub", to: "p_ai", dir: "v", label: "ground" },
];

const PIPE_BANDS: Band[] = [
  { label: "Sources", x: 30, y: 36 },
  { label: "Curate · HITL gate", x: 332, y: 36 },
  { label: "Canonical + authoring", x: 646, y: 36 },
  { label: "Build → AI", x: 950, y: 36 },
];

// Pick clean endpoints: horizontal (right↔left) for L→R flow or when the boxes vertically overlap;
// vertical (bottom↔top) otherwise. An explicit `dir` overrides the heuristic.
function edgePoints(a: Node, b: Node, dir?: "h" | "v") {
  const ac = { x: a.x + a.w / 2, y: a.y + a.h / 2 };
  const bc = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
  const useV = dir === "v" || (dir !== "h" && (b.y > a.y + a.h || a.y > b.y + b.h));
  if (useV) {
    const down = bc.y > ac.y;
    return { x1: ac.x, y1: down ? a.y + a.h : a.y, x2: bc.x, y2: down ? b.y : b.y + b.h };
  }
  const right = bc.x > ac.x;
  return { x1: right ? a.x + a.w : a.x, y1: ac.y, x2: right ? b.x : b.x + b.w, y2: bc.y };
}

function Diagram({
  nodes,
  edges,
  bands,
  viewBox,
  label,
}: {
  nodes: Node[];
  edges: Edge[];
  bands: Band[];
  viewBox: string;
  label: string;
}) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  return (
    <svg className="arch-svg" viewBox={viewBox} role="img" aria-label={label} xmlns="http://www.w3.org/2000/svg">
      <style>{`
        .arch-box { fill: var(--card); stroke: var(--rule-strong); stroke-width: 1; }
        .arch-box.acc { stroke: var(--accent-line); stroke-width: 1.4; }
        .arch-t { fill: var(--ink); font-weight: 600; font-size: 13px; }
        .arch-s { fill: var(--muted); font-size: 10px; font-family: var(--font-mono); }
        .arch-band { fill: var(--muted); font-weight: 600; font-size: 10px; font-family: var(--font-mono); letter-spacing: .14em; }
        .arch-e { stroke: var(--rule-strong); stroke-width: 1.4; fill: none; marker-end: url(#arch-arrow); }
        .arch-e.d { stroke: var(--muted); stroke-width: 1; stroke-dasharray: 4 4; }
        .arch-el { fill: var(--muted); font-size: 9.5px; font-family: var(--font-mono); }
      `}</style>
      <defs>
        <marker id="arch-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="var(--muted)" />
        </marker>
      </defs>

      {bands.map((b) => (
        <text key={b.label} className="arch-band" x={b.x} y={b.y}>
          {b.label.toUpperCase()}
        </text>
      ))}

      {edges.map((e, i) => {
        const a = byId[e.from];
        const b = byId[e.to];
        const p = edgePoints(a, b, e.dir);
        const mx = (p.x1 + p.x2) / 2;
        const my = (p.y1 + p.y2) / 2;
        return (
          <g key={i}>
            <line className={`arch-e ${e.dash ? "d" : ""}`} x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} />
            {e.label && (
              <text className="arch-el" x={mx + 4} y={my - 3}>
                {e.label}
              </text>
            )}
          </g>
        );
      })}

      {nodes.map((n) => {
        const cx = n.x + n.w / 2;
        return (
          <g key={n.id}>
            <rect className={`arch-box ${n.accent ? "acc" : ""}`} x={n.x} y={n.y} width={n.w} height={n.h} rx={10} />
            <text className="arch-t" x={cx} y={n.y + (n.sub.length > 1 ? 26 : 30)} textAnchor="middle">
              {n.title}
            </text>
            {n.sub.map((s, j) => (
              <text key={j} className="arch-s" x={cx} y={n.y + (n.sub.length > 1 ? 44 : 48) + j * 13} textAnchor="middle">
                {s}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

export function ArchitectureDiagram() {
  return (
    <div className="arch-scroll">
      <Diagram nodes={ARCH_NODES} edges={ARCH_EDGES} bands={ARCH_BANDS} viewBox="0 0 1168 850" label="System architecture diagram" />
    </div>
  );
}

export function DataPipelineDiagram() {
  return (
    <div className="arch-scroll">
      <Diagram nodes={PIPE_NODES} edges={PIPE_EDGES} bands={PIPE_BANDS} viewBox="0 0 1196 320" label="Content data pipeline diagram" />
    </div>
  );
}
